import { logger } from "@/monitoring/utils";
import contentTypes, { ContentType } from "@/view/constants/contentTypes";

export const RELATED_CARDS_COUNT = 4;

const STEM_SUBJECTS = ["mathematics", "science"];

export function isStemContentPage(
  contentType: ContentType,
  contentSubject?: string | null,
): boolean {
  if (
    contentType === contentTypes.PHET_SIMULATION ||
    contentType === contentTypes.TUVA_DATASET
  ) {
    return true;
  }

  if (contentType === contentTypes.GENERATION_GENIUS_VIDEO) {
    return STEM_SUBJECTS.includes((contentSubject ?? "").toLowerCase());
  }

  return false;
}

export function selectRelatedContent(
  results: Record<string, any>[],
  isStem: boolean,
): Record<string, any>[] {
  if (results.length === 0) return [];

  if (isStem) {
    return selectStemContent(results);
  }

  return selectDiverseContent(results);
}

function selectStemContent(
  results: Record<string, any>[],
): Record<string, any>[] {
  const selected: Record<string, any>[] = [];
  const used = new Set<number>();

  const ggIndex = results.findIndex(
    (r) =>
      r["metadata"]?.["content_type"] === contentTypes.GENERATION_GENIUS_VIDEO,
  );
  if (ggIndex !== -1) {
    selected.push(results[ggIndex]!);
    used.add(ggIndex);
  } else {
    logger.warn({
      message:
        "[RelatedContent] STEM page: no Generation Genius video found in search results",
    });
  }

  const phetIndex = results.findIndex(
    (r, i) =>
      r["metadata"]?.["content_type"] === contentTypes.PHET_SIMULATION &&
      !used.has(i),
  );
  if (phetIndex !== -1) {
    selected.push(results[phetIndex]!);
    used.add(phetIndex);
  } else {
    logger.warn({
      message:
        "[RelatedContent] STEM page: no PhET Simulation found in search results",
    });
  }

  const remaining = results.filter((_, i) => !used.has(i));
  const shuffled = shuffleArray(remaining);
  selected.push(...shuffled.slice(0, RELATED_CARDS_COUNT - selected.length));

  return selected.slice(0, RELATED_CARDS_COUNT);
}

function selectDiverseContent(
  results: Record<string, any>[],
): Record<string, any>[] {
  const byType = new Map<string, Record<string, any>[]>();

  for (const result of results) {
    const type = result["metadata"]?.["content_type"] ?? "UNKNOWN";
    if (!byType.has(type)) {
      byType.set(type, []);
    }
    byType.get(type)!.push(result);
  }

  const selected: Record<string, any>[] = [];
  const typeEntries = shuffleArray([...byType.entries()]);

  // Round-robin: pick one from each type
  for (const [, items] of typeEntries) {
    if (selected.length >= RELATED_CARDS_COUNT) break;
    const randomIndex = Math.floor(Math.random() * items.length);
    selected.push(items[randomIndex]!);
    items.splice(randomIndex, 1);
  }

  // Fill remaining slots if we have fewer types than RELATED_CARDS_COUNT
  if (selected.length < RELATED_CARDS_COUNT) {
    const remainingItems = shuffleArray(
      typeEntries.flatMap(([, items]) => items),
    );
    for (const item of remainingItems) {
      if (selected.length >= RELATED_CARDS_COUNT) break;
      selected.push(item);
    }
  }

  return selected.slice(0, RELATED_CARDS_COUNT);
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i]!;
    shuffled[i] = shuffled[j]!;
    shuffled[j] = temp;
  }
  return shuffled;
}

export function extractResults({
  data,
  limit,
  legacyFormat = false,
}: {
  data: Object | undefined;
  limit?: number;
  legacyFormat?: boolean;
}): Array<any> {
  if (!data) return [];

  let relatedContent = (data as Record<string, any>)["results"] || [];

  if (limit && relatedContent.length > limit) {
    relatedContent = relatedContent.slice(0, limit);
  }

  if (legacyFormat) {
    relatedContent = getLegacyFormat(relatedContent);
  }

  return relatedContent;
}

function getDeckContentType(contentType: ContentType) {
  let deck = contentType.toLowerCase();

  switch (contentType) {
    // NOTE: Include any other mappings as needed
    case contentTypes.ARTICLE:
      deck = "articleheader";
      break;
    default:
      break;
  }

  return deck;
}

function getLegacyFormat(data: Record<string, any>[]) {
  const DEFAULT_VALUES = {
    contentType: contentTypes.ARTICLE,
    gradeLevels: [],
    lexileLevels: [],
    liteExpiration: null,
    hasFormative: false,
    hasPowerWords: false,
    maturityRange: undefined as number[] | undefined,
    label: undefined as string | undefined,
    assetCountByContentType: undefined as { [key: string]: number } | undefined,
  };

  return data.map(
    ({
      display_date: datePublished,
      metadata: {
        content_type: contentType = DEFAULT_VALUES.contentType,
        date_lite_product_grant_expires:
          liteExpiration = DEFAULT_VALUES.liteExpiration,
        grade_levels: gradeLevels = DEFAULT_VALUES.gradeLevels,
        has_formative: hasFormative = DEFAULT_VALUES.hasFormative,
        lexile_levels: lexileLevels = DEFAULT_VALUES.lexileLevels,
        has_power_words: hasPowerWords = DEFAULT_VALUES.hasPowerWords,
        maturity_range: maturityRange = DEFAULT_VALUES.maturityRange,
        label = DEFAULT_VALUES.label,
        asset_count_by_content_type:
          assetCountByContentType = DEFAULT_VALUES.assetCountByContentType,
      } = {},
      ...item
    }) => ({
      ...item,
      content_id: item["id"],
      content_type: getDeckContentType(contentType),
      date_lite_product_grant_expires: liteExpiration,
      date_published: datePublished,
      has_formative: hasFormative,
      lexile_levels: lexileLevels,
      maturity_range: maturityRange,
      label,
      asset_count_by_content_type: assetCountByContentType,
      /**
       * NOTE: the properties below either do not exist in the new response
       * or are unreliable. Keeping them here for compatibility.
       */
      features: {
        available_in_spanish: undefined,
        has_power_words: hasPowerWords,
      },
      grade_level: gradeLevels.map((level: number) => `Grade ${level}`),
      image: item["images"]?.[0] || undefined,
      levels_ids_information: lexileLevels.map((lexile: number) => ({
        grade: undefined,
        id: undefined,
        language: undefined,
        lexile: `${lexile}L`,
      })),
      _score: undefined,
      content_maturities: undefined,
      content_provider: {
        name: "",
      },
      header_id: undefined,
      reading_skills: undefined, // metadata.reading_skills is set, but the string values are different
      slug: undefined,
      story_type: undefined,
      tags: undefined,
      visibility_markers: undefined,
      word_count: undefined,
    }),
  );
}
